import { Badge, Banner, Button, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ICONS } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { useCategories } from '../../hooks/useCategories';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { BrandLogo } from '../../components/BrandLogo';
import { SkeletonText } from '../../components/ui/Skeleton';

interface Page { id: string; pageId: string; name: string; status: string }
interface Mapping { key: string; label: string; target: string | null }
interface Automation {
  id: string; pageRowId: string; name: string; formId: string; formName: string | null; fieldMappings: Mapping[];
  categoryId: string | null; sequenceId: string | null; welcomeSession: string | null; welcomeMessage: string | null; active: boolean; leadsCount: number;
}
interface Lead { id: string; automationId: string; status: string; error: string | null; createdAt: string; data: Record<string, string> }
interface Form { id: string; name: string; status: string; questions?: Array<{ key: string; label: string; type: string }> }
type Draft = Omit<Automation, 'id' | 'leadsCount'> & { id?: string };

const STANDARD: Record<string, string> = { full_name: 'nome', first_name: 'nome', phone_number: 'telefone', email: 'email' };
const LEAD_STATUS: Record<string, { label: string; color: 'green' | 'blue' | 'red' | 'neutral' }> = {
  CREATED: { label: 'Contato novo', color: 'green' }, UPDATED: { label: 'Contato atualizado', color: 'blue' }, FAILED: { label: 'Falhou', color: 'red' }, PENDING: { label: 'Processando', color: 'neutral' },
};
const NONE = '__none__';

/** Ferramentas › Leads do Facebook (Lead Ads) */
export function LeadAdsPage() {
  const { can } = useTenant();
  const canConnect = can('connections:manage');
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const { categories } = useCategories({ pageSize: 200 });
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ configured: boolean; pages: Page[]; automations: Automation[]; leads: Lead[] } | null>(null);
  const [sequences, setSequences] = useState<Array<{ id: string; name: string }>>([]);
  const [sessions, setSessions] = useState<Array<{ name: string; displayName?: string | null }>>([]);
  const [fields, setFields] = useState<Array<{ key: string; name: string }>>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [forms, setForms] = useState<Form[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Automation | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/tools/lead-ads');
    if (r.ok) setData(await r.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchApi('/api/sequences').then((r) => (r.ok ? r.json() : { sequences: [] })).then((d) => setSequences(d.sequences || [])).catch(() => undefined);
    fetchApi('/api/connections/whatsapp-sessions').then((r) => (r.ok ? r.json() : [])).then(setSessions).catch(() => undefined);
    fetchApi('/api/custom-fields').then((r) => (r.ok ? r.json() : { fields: [] })).then((d) => setFields(d.fields || [])).catch(() => undefined);
  }, [fetchApi]);

  useEffect(() => {
    const ok = params.get('conectadas'); const err = params.get('erro'); const taken = params.get('emOutra');
    if (ok) toast.success(`${ok} página${ok === '1' ? '' : 's'} conectada${ok === '1' ? '' : 's'}`);
    if (taken) toast.error(`${taken} página(s) já estão em outra organização`);
    if (err) toast.error(err);
    if (ok || err) setParams({}, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  // Formulários da página escolhida na automação
  useEffect(() => {
    if (!draft?.pageRowId) { setForms([]); return; }
    fetchApi(`/api/tools/lead-ads/pages/${draft.pageRowId}/forms`).then((r) => (r.ok ? r.json() : { forms: [] })).then((d) => setForms(d.forms || [])).catch(() => setForms([]));
  }, [fetchApi, draft?.pageRowId]);

  const connect = async () => {
    const r = await fetchApi('/api/tools/lead-ads/connect');
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível iniciar'); return; }
    window.location.href = j.url;
  };

  const call = async (url: string, init: RequestInit, okMsg: string) => {
    const r = await fetchApi(url, init);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar'); return false; }
    toast.success(okMsg); load(); return true;
  };

  const chooseForm = (formId: string) => {
    if (!draft) return;
    const f = forms.find((x) => x.id === formId);
    setDraft({
      ...draft, formId, formName: f?.name ?? 'Todos os formulários',
      fieldMappings: f?.questions?.map((q) => ({ key: q.key, label: q.label, target: STANDARD[q.key] ?? (fields.some((x) => x.key === q.key) ? `campo:${q.key}` : null) })) ?? [],
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const ok = await call(draft.id ? `/api/tools/lead-ads/automations/${draft.id}` : '/api/tools/lead-ads/automations', { method: draft.id ? 'PUT' : 'POST', body: JSON.stringify(draft) }, draft.id ? 'Automação salva' : 'Automação criada');
    setSaving(false);
    if (ok) setDraft(null);
  };

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;
  const connected = data.pages.filter((p) => p.status === 'CONNECTED');
  const autoName = (id: string) => data.automations.find((a) => a.id === id)?.name ?? '';

  const pageOptions = connected.map((p) => ({ value: p.id, label: p.name }));
  const formOptions = [
    { value: '*', label: 'Todos os formulários da página' },
    ...forms.map((f) => ({ value: f.id, label: `${f.name}${f.status !== 'ACTIVE' ? ` (${f.status.toLowerCase()})` : ''}` })),
  ];
  const categoryOptions = [
    { value: NONE, label: 'Nenhuma categoria' },
    ...categories.map((c) => ({ value: c.id, label: c.nome })),
  ];
  const sequenceOptions = [
    { value: NONE, label: 'Nenhuma sequência' },
    ...sequences.map((s) => ({ value: s.id, label: s.name })),
  ];
  const sessionOptions = [
    { value: NONE, label: 'Nenhuma conexão' },
    ...sessions.map((s) => ({ value: s.name, label: s.displayName || s.name })),
  ];

  if (draft) {
    return (
      <FormPage
        backTo="/ferramentas/lead-ads"
        backLabel="Lead Ads"
        onBack={() => setDraft(null)}
        title={draft?.id ? 'Editar formulário ligado' : 'Ligar formulário do anúncio'}
        description="Cada lead do formulário vira contato na organização."
        submitLabel={'Salvar'}
        submitting={saving}
        submitDisabled={!draft?.name.trim()}
        onSubmit={save}
        onCancel={() => setDraft(null)}
      >
        <FormSection title="Detalhes">


        {draft && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Nome">
                <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} placeholder="Ex.: Orçamento Black Friday" />
              </Field>
              <Field label="Página">
                <Select value={(pageOptions.find((o) => o.value === draft.pageRowId) ?? null)?.value ?? null} items={pageOptions} onValueChange={(value: string | null) => setDraft({ ...draft, pageRowId: value ?? '', formId: '*', fieldMappings: [] })} disabled={!!draft.id} />
              </Field>
              <Field label="Formulário">
                <Select value={(formOptions.find((o) => o.value === draft.formId) ?? null)?.value ?? null} items={formOptions} onValueChange={(value: string | null) => chooseForm(value ?? '*')} />
              </Field>
            </div>
            {draft.fieldMappings.length > 0 ? (
              <div className="space-y-2">
                <div className="font-semibold">Para onde vai cada resposta</div>
                {draft.fieldMappings.map((m, i) => (
                  <div key={m.key} className="flex items-center gap-3" style={{ fontSize: 13 }}>
                    <span style={{ minWidth: 200 }}>{m.label}</span>
                    <ICONS.ArrowRight size={16} />
                    <select aria-label={`Destino de ${m.label}`} value={m.target ?? ''} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, minWidth: 220 }}
                      onChange={(e) => setDraft({ ...draft, fieldMappings: draft.fieldMappings.map((x, j) => (j === i ? { ...x, target: e.target.value || null } : x)) })}>
                      <option value="">Não guardar</option>
                      <option value="nome">Nome</option>
                      <option value="telefone">Telefone (WhatsApp)</option>
                      <option value="email">E-mail</option>
                      {fields.map((f) => <option key={f.key} value={`campo:${f.key}`}>Campo: {f.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs !text-[var(--text-muted)]">Todos os formulários: nome, telefone e e-mail vão para o contato; as demais perguntas que tiverem o mesmo nome de um campo personalizado também.</div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Categoria do contato (opcional)">
                <Select value={(categoryOptions.find((o) => o.value === (draft.categoryId ?? NONE)) ?? null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => setDraft({ ...draft, categoryId: value === NONE ? null : (value ?? null) })} />
              </Field>
              <Field label="Inscrever na sequência (opcional)">
                <Select value={(sequenceOptions.find((o) => o.value === (draft.sequenceId ?? NONE)) ?? null)?.value ?? null} items={sequenceOptions} onValueChange={(value: string | null) => setDraft({ ...draft, sequenceId: value === NONE ? null : (value ?? null) })} />
              </Field>
            </div>
            <Field label="Conexão que manda a boas-vindas">
              <Select value={(sessionOptions.find((o) => o.value === (draft.welcomeSession ?? NONE)) ?? null)?.value ?? null} items={sessionOptions} onValueChange={(value: string | null) => setDraft({ ...draft, welcomeSession: value === NONE ? null : (value ?? null) })} />
            </Field>
            <InputArea label="Mensagem de boas-vindas (vazio = não manda)" onChange={(e) => setDraft({ ...draft, welcomeMessage: e.target.value })} rows={3} value={draft.welcomeMessage ?? ''} />
            </div>
)}
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/ferramentas" className="tools-back"><ICONS.ArrowLeft size={16} /> Ferramentas</Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3" style={{ marginBottom: 4 }}>
              <BrandLogo brand="facebook" size={32} rounded={8} />
              <h2 className="text-xl font-semibold" style={{ fontSize: 24, fontWeight: 600 }}>Leads do Facebook</h2>
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              Quem preenche o formulário do seu anúncio (Lead Ads) vira contato na hora, recebe a boas-vindas no WhatsApp em segundos e pode entrar numa sequência.
            </div>
          </div>
          {canConnect && data.configured && (
            <Button variant="primary" onClick={connect}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <BrandLogo brand="facebook" size={16} rounded={4} /> Conectar páginas
              </span>
            </Button>
          )}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!data.configured && <Banner variant="alert" description={<>O app da Meta ainda não foi configurado pelo administrador da plataforma.</>} />}

        <section className="space-y-3">
          <h4 className="text-base font-semibold">Páginas</h4>
          {data.pages.length === 0 && <Banner variant="default" description={<>Nenhuma página conectada. No login, escolha as páginas que têm anúncios de cadastro.</>} />}
          <div className="flex flex-wrap gap-2">
            {data.pages.map((p) => (
              <span key={p.id} className="brand-card flex items-center gap-2" style={{ padding: '8px 12px' }}>
                <strong>{p.name}</strong>
                {p.status === 'CONNECTED' ? <Badge variant="green">Conectada</Badge> : <Badge variant="neutral">Desconectada</Badge>}
                {canConnect && p.status === 'CONNECTED' && (
                  <Button variant="ghost" shape="square" icon={ICONS.X} aria-label={`Desconectar ${p.name}`} onClick={() => call(`/api/tools/lead-ads/pages/${p.id}`, { method: 'DELETE' }, 'Página desconectada')} />
                )}
              </span>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-base font-semibold">Formulários ligados</h4>
            {canEdit && connected.length > 0 && (
              <Button variant="secondary" icon={ICONS.Plus} onClick={() => setDraft({ pageRowId: connected[0].id, name: '', formId: '*', formName: 'Todos os formulários', fieldMappings: [], categoryId: null, sequenceId: null, welcomeSession: null, welcomeMessage: 'Oi {{primeiro_nome}}! Recebemos seu cadastro e já vamos falar com você por aqui. 😊', active: true })}>
                Ligar formulário
              </Button>
            )}
          </div>
          {data.automations.map((a) => (
            <div key={a.id} className="brand-card flex items-center justify-between gap-4">
              <div style={{ minWidth: 0 }}>
                <div className="font-semibold">{a.name}</div>
                <div className="text-xs !text-[var(--text-muted)]">
                  {data.pages.find((p) => p.id === a.pageRowId)?.name} · {a.formName || (a.formId === '*' ? 'Todos os formulários' : a.formId)} · {a.leadsCount} leads
                  {a.sequenceId ? ` · sequência ${sequences.find((s) => s.id === a.sequenceId)?.name ?? ''}` : ''}{a.welcomeMessage ? ' · boas-vindas' : ''}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <Switch onCheckedChange={(checked: boolean) => call(`/api/tools/lead-ads/automations/${a.id}`, { method: 'PUT', body: JSON.stringify({ active: checked }) }, checked ? 'Ligada' : 'Desligada')} aria-label={`Ativar ${a.name}`} checked={a.active} />
                  <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label={`Editar ${a.name}`} onClick={() => setDraft({ ...a })} />
                  <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Excluir ${a.name}`} onClick={() => setDeleting(a)} />
                </div>
              )}
            </div>
          ))}
        </section>

        {data.leads.length > 0 && (
          <section className="brand-card space-y-2">
            <h4 className="text-base font-semibold">Últimos leads</h4>
            {data.leads.map((l) => {
              const s = LEAD_STATUS[l.status] ?? { label: l.status, color: 'neutral' as const };
              return (
                <div key={l.id} className="flex items-center justify-between gap-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 6, fontSize: 13 }}>
                  <span>{l.data.full_name || l.data.first_name || '—'} · {l.data.phone_number || 'sem telefone'} · {autoName(l.automationId)} · {new Date(l.createdAt).toLocaleString('pt-BR')}</span>
                  <span className="flex items-center gap-2">{l.error && <small style={{ color: 'var(--crm-text-error)' }}>{l.error}</small>}<Badge variant={s.color}>{s.label}</Badge></span>
                </div>
              );
            })}
          </section>
        )}
      </div>



      <DangerConfirmModal open={!!deleting} title="Desligar formulário" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) await call(`/api/tools/lead-ads/automations/${deleting.id}`, { method: 'DELETE' }, 'Automação excluída'); setDeleting(null); }}>
        Os próximos leads de <strong>{deleting?.name}</strong> deixam de virar contato. Os contatos já criados continuam.
      </DangerConfirmModal>
    </>
  );
}
